import { useState, useEffect } from "react";
import { useEstimatePrice } from "../hooks/useEstimatePrice.ts";
import { PriceEstimationRequest } from "../models/priceEstimationRequest.ts";
import { PriceResponse } from "../models/priceResponse.ts";
import { Typography, TextField, Grid, Box } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import PriceResponseView from "./PriceResponseView.tsx";
import MechanicsForm from "./Mechanics.tsx";

const customTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#0066cc",
    },
    secondary: {
      main: "#009966",
    },
    background: {
      default: "#fff",
      paper: "#fafafa",
    },
    text: {
      primary: "#222",
      secondary: "#555",
    },
  },
  typography: {
    fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    h4: {
      fontWeight: 600,
      color: "#0066cc",
    },
    h6: {
      fontWeight: 500,
      color: "#222",
    },
    body1: {
      color: "#333",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 4,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#009966",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#009966",
            borderWidth: 2,
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: "#009966",
          },
        },
      },
    },
  },
});

const PriceEstimationForm = () => {
  const [request, setRequest] = useState<PriceEstimationRequest>({
    mechanics: {
      housing: "m2",
      contacting: "stack",
      switch: true,
      user_interface: true,
      dut_height: 1,
      dut_width: 1,
    },
    electronics: {
      relais: 0,
      voltages: 0,
      currents: 0,
      two_quadrant_power_supply: 0,
    },
    communication: {
      rs232: 0,
      rs485: 0,
      usb: 0,
      sd_card: 0,
    },
    flasher: {
      stlinks: 0,
    },
    number_of_contacts: 0,
    number_of_duts: 1,
    number_of_systems: 1,
  });
  const [respone, setResponse] = useState<PriceResponse | null>(null);
  const { estimatePrice } = useEstimatePrice();

  const handleSubmit = async (data = request) => {
    const tempResponse = await estimatePrice(data);
    setResponse(tempResponse);
  };

  const handleChange = (field: keyof PriceEstimationRequest, value: any) => {
    setRequest(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    handleSubmit();
  }, [request]);

  return (
    <ThemeProvider theme={customTheme}>
      <Typography variant="h4" gutterBottom>
        Richtpreis Rechner
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12 }}>
          <MechanicsForm
            data={request.mechanics}
            showDutSize={false}
            onChange={(field, value) =>
              handleChange("mechanics", { ...request.mechanics, [field]: value })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Typography variant="h6" gutterBottom>Testpunkte</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl Testpunkte (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 1 }}
            value={request.number_of_contacts}
            onChange={(e) =>
              handleChange("number_of_contacts", parseInt(e.target.value))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Typography variant="h6" gutterBottom>Elektronik</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl Relais (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.electronics.relais}
            onChange={(e) =>
              handleChange("electronics", {
                ...request.electronics,
                relais: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl Spannungsmessungen (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.electronics.voltages}
            onChange={(e) =>
              handleChange("electronics", {
                ...request.electronics,
                voltages: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl Strommessungen (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.electronics.currents}
            onChange={(e) =>
              handleChange("electronics", {
                ...request.electronics,
                currents: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl Zwei-Quadrantennetzteile (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.electronics.two_quadrant_power_supply}
            onChange={(e) =>
              handleChange("electronics", {
                ...request.electronics,
                two_quadrant_power_supply: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Typography variant="h6" gutterBottom>Kommunikation</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="RS-232 Ports (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.communication.rs232}
            onChange={(e) =>
              handleChange("communication", {
                ...request.communication,
                rs232: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="RS-485 Ports (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.communication.rs485}
            onChange={(e) =>
              handleChange("communication", {
                ...request.communication,
                rs485: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="USB Ports (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.communication.usb}
            onChange={(e) =>
              handleChange("communication", {
                ...request.communication,
                usb: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="SD-Karten Slots (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.communication.sd_card}
            onChange={(e) =>
              handleChange("communication", {
                ...request.communication,
                sd_card: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Typography variant="h6" gutterBottom>Flasher</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="ST-Links (pro DUT)"
            type="number"
            inputProps={{ step: 1, min: 0 }}
            value={request.flasher.stlinks}
            onChange={(e) =>
              handleChange("flasher", {
                ...request.flasher,
                stlinks: parseInt(e.target.value),
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Typography variant="h6" gutterBottom>System</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Anzahl DUTs pro Prüfsystem"
            type="number"
            inputProps={{ step: 1, min: 1 }}
            value={request.number_of_duts}
            onChange={(e) =>
              handleChange("number_of_duts", parseInt(e.target.value))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box mt={2} />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <PriceResponseView 
            label="Richtpreis"
            calculation={respone} 
            numberOfSystems={request.number_of_systems} 
            setNumberOfSystems={(value) => handleChange("number_of_systems", value)} 
          />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default PriceEstimationForm;

